import React, { useEffect, useMemo, useState } from 'react'
import { Accordion, Button, Form, Modal, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { MATCH_NONE } from '../../constants/matching-algorithms'
import { customFieldService } from '../../services/custom-field.service'
import { documentTypeService } from '../../services/document-type.service'
import { documentWorkflowDefinitionService } from '../../services/document-workflow-definition.service'
import { permissionsService } from '../../services/permissions.service'
import { CustomField, DocumentType, DocumentTypeCustomFieldConfig } from '../../types'
import { getMatchingAlgorithms, isPatternRequired } from '../../utils/matchingAlgorithms'
import CustomFieldsQueryDropdown, {
  CustomFieldQueriesModel,
  CustomFieldQueryElement,
  CustomFieldQueryElementType,
} from '../common/CustomFieldsQueryDropdown'
import { CheckboxInput, EditDialog, FormField, SelectInput, TextInput } from '../common/EditDialog'
import { PermissionsForm, PermissionsFormValue } from '../common/PermissionsForm'
import WordTemplatesSection from './WordTemplatesSection'
import './DocumentTypeEditDialog.scss'

const visibilityQueryToModel = (query: any): CustomFieldQueriesModel => {
  const model = new CustomFieldQueriesModel()
  if (query === null || query === undefined || query === '') return model

  let parsed = query
  if (typeof query === 'string') {
    try {
      parsed = JSON.parse(query)
    } catch {
      return model
    }
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return model

  const parseElement = (item: any): CustomFieldQueryElement | null => {
    if (Array.isArray(item)) {
      if (item.length === 3) {
        return {
          type: CustomFieldQueryElementType.Atom,
          atom: { field: item[0], operator: item[1], value: item[2] },
        }
      }
      if (item.length === 2 && Array.isArray(item[1])) {
        const [operator, elements] = item
        return {
          type: CustomFieldQueryElementType.Expression,
          expression: { operator, elements: elements.map(parseElement).filter(Boolean) as CustomFieldQueryElement[] },
        }
      }
    }
    return null
  }

  if (parsed.length === 2 && Array.isArray(parsed[1])) {
    const [operator, elements] = parsed
    model.logicalOperator = operator
    const parsedElements = elements.map(parseElement).filter(Boolean) as CustomFieldQueryElement[]
    model.queries = [{ type: CustomFieldQueryElementType.Expression, expression: { operator, elements: parsedElements } }]
  } else {
    const parsedElements = parsed.map(parseElement).filter(Boolean) as CustomFieldQueryElement[]
    if (parsedElements.length > 0) {
      model.queries = [{ type: CustomFieldQueryElementType.Expression, expression: { operator: model.logicalOperator, elements: parsedElements } }]
    }
  }
  return model
}

const modelToVisibilityQuery = (model: CustomFieldQueriesModel): any => {
  const serialized = model.serialize()
  if (!serialized) return null
  try {
    return JSON.parse(serialized)
  } catch {
    return null
  }
}

const VisibilityQueryCell: React.FC<{
  config: DocumentTypeCustomFieldConfig
  allowedFieldIds: number[]
  onChange: (query: any) => void
}> = ({ config, allowedFieldIds, onChange }) => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [model, setModel] = useState<CustomFieldQueriesModel>(() => visibilityQueryToModel(config.visibility_query))

  useEffect(() => {
    setModel(visibilityQueryToModel(config.visibility_query))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.custom_field, config.visibility_query])

  const hasQuery = config.visibility_query != null

  const openModal = () => {
    setModel(visibilityQueryToModel(config.visibility_query))
    setShowModal(true)
  }

  const handleSave = () => {
    onChange(modelToVisibilityQuery(model))
    setShowModal(false)
  }

  const handleClear = () => {
    setModel(new CustomFieldQueriesModel())
    onChange(null)
    setShowModal(false)
  }

  return (
    <>
      <div className="d-flex align-items-center gap-1">
        <Button variant={hasQuery ? 'primary' : 'outline-primary'} size="sm" onClick={openModal}>
          <i className="bi bi-eye me-1" />
          {hasQuery ? t('visibility_condition_set') : t('visibility_condition')}
        </Button>
        {hasQuery && (
          <Button
            variant="link"
            size="sm"
            className="text-danger p-0"
            title={t('clear')}
            onClick={handleClear}
          >
            <i className="bi bi-x-circle"></i>
          </Button>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
        backdrop="static"
        enforceFocus={false}
        className="document-type-visibility-query-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('visibility_condition')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">{t('visibility_condition_help')}</p>
          {showModal && (
            <CustomFieldsQueryDropdown
              selectionModel={model}
              onSelectionModelChange={setModel}
              allowedCustomFieldIds={allowedFieldIds}
              useDropdown={false}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={handleClear}>
            {t('clear')}
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {t('save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

interface DocumentTypeEditDialogProps {
  object?: DocumentType | null
  isCreating: boolean
  onSuccess: () => void
  onError: (error: any) => void
  onClose: () => void
}

export const DocumentTypeEditDialog: React.FC<DocumentTypeEditDialogProps> = ({ object, isCreating, onSuccess, onError, onClose }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<Partial<DocumentType>>({})
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [workflowDefinitions, setWorkflowDefinitions] = useState<Array<{ id: number; name: string }>>([])
  const [loadingFields, setLoadingFields] = useState(false)
  const [loadingDefinitions, setLoadingDefinitions] = useState(false)
  const [showSelectAttributesDialog, setShowSelectAttributesDialog] = useState(false)
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null)

  useEffect(() => {
    if (object) {
      // Map permissions from object to formData
      // Backend returns permissions as { view: {...}, change: {...} }
      // which we need to map to set_permissions for the form
      setFormData({
        ...object,
        owner: object.owner || null,
        custom_fields: object.custom_fields || [],
        workflow_definitions: object.workflow_definitions || [],
        custom_field_configs:
          object.custom_field_configs?.map((cfg, idx) => ({
            ...cfg,
            order: cfg.order ?? idx,
          })) ||
          (object.custom_fields || []).map((id, idx) => ({
            custom_field: id,
            order: idx,
            required: false,
            override_name: null,
            override_placeholder: null,
            override_default_value: null,
          })),
        set_permissions: object.permissions || {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    } else {
      setFormData({
        name: '',
        matching_algorithm: MATCH_NONE,
        match: '',
        is_insensitive: true,
        owner: null,
        custom_fields: [],
        workflow_definitions: [],
        custom_field_configs: [],
        set_permissions: {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    }
  }, [object])

  useEffect(() => {
    const loadCustomFields = async () => {
      setLoadingFields(true)
      try {
        const response = await customFieldService.listAll()
        setCustomFields(response.results || [])
      } catch (error) {
        console.error('Error loading custom fields:', error)
      } finally {
        setLoadingFields(false)
      }
    }
    loadCustomFields()
  }, [])

  useEffect(() => {
    const loadWorkflowDefinitions = async () => {
      setLoadingDefinitions(true)
      try {
        const response = await documentWorkflowDefinitionService.listAll()
        setWorkflowDefinitions(response.results || [])
      } catch (error) {
        console.error('Error loading workflow definitions:', error)
      } finally {
        setLoadingDefinitions(false)
      }
    }
    loadWorkflowDefinitions()
  }, [])

  useEffect(() => {
    if (showSelectAttributesDialog) {
      document.body.classList.add('document-type-select-attributes-open')
    } else {
      document.body.classList.remove('document-type-select-attributes-open')
    }
    return () => {
      document.body.classList.remove('document-type-select-attributes-open')
    }
  }, [showSelectAttributesDialog])

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const sortedConfigs = useMemo(() => {
    const configs = (formData.custom_field_configs || []) as DocumentTypeCustomFieldConfig[]
    return [...configs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }, [formData.custom_field_configs])

  const allowedParentIdsByField = useMemo(() => {
    const eligible = sortedConfigs
      .filter((cfg) => {
        const field = customFields.find((f) => f.id === cfg.custom_field)
        return field && field.data_type !== 'separator' && field.data_type !== 'counter'
      })
      .map((cfg) => cfg.custom_field)
    const map = new Map<number, number[]>()
    sortedConfigs.forEach((cfg) => {
      map.set(
        cfg.custom_field,
        eligible.filter((id) => id !== cfg.custom_field)
      )
    })
    return map
  }, [sortedConfigs, customFields])

  const syncConfigs = (configs: DocumentTypeCustomFieldConfig[]) => {
    const normalized = configs.map((cfg, idx) => ({ ...cfg, order: idx }))
    setFormData((prev) => ({
      ...prev,
      custom_field_configs: normalized,
      custom_fields: normalized.map((cfg) => cfg.custom_field),
    }))
  }

  const getCustomFieldById = (fieldId: number) => customFields.find((f) => f.id === fieldId)

  const handleConfigChange = (fieldId: number, patch: Partial<DocumentTypeCustomFieldConfig>) => {
    const configs = sortedConfigs.map((cfg) => (cfg.custom_field === fieldId ? { ...cfg, ...patch } : cfg))
    syncConfigs(configs)
  }

  const handleRemoveFieldFromSelection = (fieldId: number) => {
    syncConfigs(sortedConfigs.filter((cfg) => cfg.custom_field !== fieldId))
  }

  const handleFieldToggleInDialog = (fieldId: number, checked: boolean) => {
    const existing = sortedConfigs.find((cfg) => cfg.custom_field === fieldId)
    if (checked && !existing) {
      const field = getCustomFieldById(fieldId)
      syncConfigs([
        ...sortedConfigs,
        {
          custom_field: fieldId,
          order: sortedConfigs.length,
          required: false,
          override_name: null,
          override_placeholder: null,
          override_default_value: field?.default_value ?? null,
        },
      ])
      return
    }
    if (!checked && existing) {
      handleRemoveFieldFromSelection(fieldId)
    }
  }

  const moveConfigRow = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= sortedConfigs.length || to >= sortedConfigs.length) return
    const items = [...sortedConfigs]
    const [moved] = items.splice(from, 1)
    items.splice(to, 0, moved)
    syncConfigs(items)
  }

  const renderDefaultValueInput = (cfg: DocumentTypeCustomFieldConfig, field: CustomField) => {
    const value = cfg.override_default_value
    if (field.data_type === 'boolean') {
      return (
        <Form.Check
          type="checkbox"
          checked={!!value}
          onChange={(e) => handleConfigChange(cfg.custom_field, { override_default_value: e.target.checked })}
        />
      )
    }
    if (field.data_type === 'date') {
      return (
        <Form.Control
          type="date"
          size="sm"
          value={value || ''}
          onChange={(e) => handleConfigChange(cfg.custom_field, { override_default_value: e.target.value || null })}
        />
      )
    }
    if (field.data_type === 'integer') {
      return (
        <Form.Control
          type="number"
          size="sm"
          value={value ?? ''}
          onChange={(e) =>
            handleConfigChange(cfg.custom_field, {
              override_default_value: e.target.value === '' ? null : parseInt(e.target.value, 10),
            })
          }
        />
      )
    }
    if (field.data_type === 'float') {
      return (
        <Form.Control
          type="number"
          size="sm"
          step="0.01"
          value={value ?? ''}
          onChange={(e) =>
            handleConfigChange(cfg.custom_field, {
              override_default_value: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
        />
      )
    }
    if (field.data_type === 'select') {
      const options = field.extra_data?.select_options || []
      return (
        <Form.Select
          size="sm"
          value={value || ''}
          onChange={(e) => handleConfigChange(cfg.custom_field, { override_default_value: e.target.value || null })}
        >
          <option value="">{t('select_option')}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )
    }
    return (
      <Form.Control
        type="text"
        size="sm"
        value={value ?? ''}
        onChange={(e) => handleConfigChange(cfg.custom_field, { override_default_value: e.target.value === '' ? null : e.target.value })}
      />
    )
  }

  const validateForm = (data: Partial<DocumentType>): string[] => {
    const errors: string[] = []

    if (!data.name || data.name.trim() === '') {
      errors.push('name:Name is required')
    }

    return errors
  }

  const getFormFields = (errors?: Record<string, string>) => {
    const patternRequired = formData.matching_algorithm !== undefined && isPatternRequired(formData.matching_algorithm)
    const showPermissions = !object || permissionsService.currentUserOwnsObject(object)

    return (
      <>
        <FormField label={t('name')} required error={errors?.name}>
          <TextInput
            value={formData.name || ''}
            onChange={(value) => handleInputChange('name', value)}
            placeholder={t('enter_name')}
            required
            autoComplete="off"
            error={errors?.name}
          />
        </FormField>

        <FormField label={t('matching_algorithm')}>
          <SelectInput
            value={formData.matching_algorithm}
            onChange={(value) => handleInputChange('matching_algorithm', value !== null ? parseInt(value) : null)}
            options={getMatchingAlgorithms(t)}
          />
        </FormField>

        {patternRequired && (
          <>
            <FormField label={t('matching_pattern')}>
              <TextInput
                value={formData.match || ''}
                onChange={(value) => handleInputChange('match', value)}
                placeholder={t('enter_matching_pattern')}
              />
            </FormField>

            <FormField label={t('case_insensitive')}>
              <CheckboxInput
                checked={formData.is_insensitive || false}
                onChange={(checked) => handleInputChange('is_insensitive', checked)}
                label={t('case_insensitive')}
              />
            </FormField>
          </>
        )}

        {/* Workflow definitions (allowed for this document type) – transitions are defined in workflow definitions */}
        <Accordion className="mt-3 mb-3">
          <Accordion.Item eventKey="workflow-definitions">
            <Accordion.Header>
              {t('document_workflow.workflow_definitions_for_type')} ({formData.workflow_definitions?.length || 0})
            </Accordion.Header>
            <Accordion.Body>
              <p className="text-muted small mb-2">{t('document_workflow.workflow_definitions_for_type_help')}</p>
              {loadingDefinitions ? (
                <div className="text-center py-2">
                  <div className="spinner-border spinner-border-sm" role="status" />
                </div>
              ) : workflowDefinitions.length === 0 ? (
                <p className="text-muted mb-0 small">{t('document_workflow.no_workflow_definitions')}</p>
              ) : (
                <div className="list-group">
                  {workflowDefinitions.map((wd: { id: number; name: string }) => {
                    const isSelected = formData.workflow_definitions?.includes(wd.id) ?? false
                    return (
                      <div key={wd.id} className="list-group-item list-group-item-action">
                        <Form.Check
                          type="checkbox"
                          id={`workflow-def-${wd.id}`}
                          label={wd.name}
                          checked={isSelected}
                          onChange={() => {
                            const current = formData.workflow_definitions || []
                            const next = isSelected ? current.filter((id) => id !== wd.id) : [...current, wd.id]
                            handleInputChange('workflow_definitions', next)
                          }}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="0">
            <Accordion.Header>
              {t('attributes')} ({sortedConfigs.length})
            </Accordion.Header>
            <Accordion.Body>
              {loadingFields ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">{t('loading') || 'Učitavanje...'}</span>
                  </div>
                </div>
              ) : (
                <FormField label={`${t('attributes')} (${sortedConfigs.length})`}>
                  <div className="d-flex justify-content-end mb-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setShowSelectAttributesDialog(true)} disabled={loadingFields}>
                      <i className="bi bi-list-check me-1"></i>
                      {t('select_attributes') || 'Odaberi atribute'}
                    </Button>
                  </div>
                  <div className="table-responsive border rounded">
                    <Table size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>{t('name')}</th>
                          <th>{t('default_value')}</th>
                          <th>{t('placeholder_text')}</th>
                          <th style={{ width: 110 }}>{t('required_column')}</th>
                          <th style={{ width: 160 }} title={t('visibility_condition_help')}>
                            {t('visibility_column')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedConfigs.length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-3">
                              {t('no_custom_fields_available') || 'Nema dostupnih atributa'}
                            </td>
                          </tr>
                        )}
                        {sortedConfigs.map((cfg, index) => {
                          const field = getCustomFieldById(cfg.custom_field)
                          if (!field) return null
                          const displayName = cfg.override_name || field.name
                          const isSeparator = field.data_type === 'separator'
                          const isCounter = field.data_type === 'counter'
                          return (
                            <tr
                              key={cfg.custom_field}
                              draggable
                              onDragStart={() => setDragFromIndex(index)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => {
                                if (dragFromIndex !== null) {
                                  moveConfigRow(dragFromIndex, index)
                                  setDragFromIndex(null)
                                }
                              }}
                              className={isSeparator ? 'attribute-config-separator-row' : undefined}
                            >
                              <td className="text-center text-muted" title={t('drag_to_reorder') || 'Drag to reorder'}>
                                <i className="bi bi-grip-vertical"></i>
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  value={displayName}
                                  onChange={(e) => handleConfigChange(cfg.custom_field, { override_name: e.target.value })}
                                />
                              </td>
                              <td>{isSeparator || isCounter ? <span className="text-muted small">—</span> : renderDefaultValueInput(cfg, field)}</td>
                              <td>
                                {isSeparator || isCounter ? (
                                  <span className="text-muted small">—</span>
                                ) : (
                                  <Form.Control
                                    size="sm"
                                    value={cfg.override_placeholder || field.placeholder || ''}
                                    onChange={(e) =>
                                      handleConfigChange(cfg.custom_field, {
                                        override_placeholder: e.target.value === '' ? null : e.target.value,
                                      })
                                    }
                                  />
                                )}
                              </td>
                              <td className="text-center">
                                {isSeparator || isCounter ? (
                                  <span className="text-muted small">—</span>
                                ) : (
                                  <Form.Check
                                    type="checkbox"
                                    checked={!!cfg.required}
                                    onChange={(e) => handleConfigChange(cfg.custom_field, { required: e.target.checked })}
                                  />
                                )}
                              </td>
                              <td>
                                {isSeparator || isCounter ? (
                                  <span className="text-muted small">—</span>
                                ) : (
                                  <VisibilityQueryCell
                                    config={cfg}
                                    allowedFieldIds={allowedParentIdsByField.get(cfg.custom_field) || []}
                                    onChange={(query) => handleConfigChange(cfg.custom_field, { visibility_query: query })}
                                  />
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                </FormField>
              )}
            </Accordion.Body>
          </Accordion.Item>

        </Accordion>

        {object?.id ? (
          <WordTemplatesSection documentType={object} />
        ) : (
          <Accordion className="mt-3 mb-3">
            <Accordion.Item eventKey="word-templates-new">
              <Accordion.Header>
                <span>
                  {t('document_templates') || 'Šabloni dokumenta'} (0)
                </span>
              </Accordion.Header>
              <Accordion.Body>
                <div className="text-muted small mb-3">
                  <i className="bi bi-info-circle me-2" />
                  {t('save_document_type_before_templates') || 'Sačuvajte tip dokumenta pre dodavanja Word šablona.'}
                </div>
                <Button variant="outline-primary" disabled className="w-100 py-2 fw-semibold" style={{ borderStyle: 'dashed' }}>
                  <i className="bi bi-plus-lg me-2" />
                  {t('add_new_template') || 'Dodaj novi šablon'}
                </Button>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {showPermissions && (
          <PermissionsForm
            value={{
              owner: formData.owner || null,
              set_permissions: formData.set_permissions || {
                view: { users: [], groups: [] },
                change: { users: [], groups: [] },
              },
            }}
            onChange={(permissionsValue: PermissionsFormValue) => {
              setFormData(
                (prev) =>
                  ({
                    ...prev,
                    owner: permissionsValue.owner,
                    set_permissions: permissionsValue.set_permissions,
                  }) as Partial<DocumentType>
              )
            }}
            accordion={true}
          />
        )}
      </>
    )
  }

  const getTitle = () => {
    return isCreating ? t('create_new_document_type') : t('edit_document_type')
  }

  const selectedColumnLabel = (() => {
    const label = t('selected') || 'Selected'
    return label.charAt(0).toUpperCase() + label.slice(1)
  })()

  return (
    <>
      <EditDialog
        object={object}
        isCreating={isCreating}
        onSuccess={onSuccess}
        onError={onError}
        onClose={onClose}
        service={documentTypeService}
        formData={formData}
        getFormFields={getFormFields}
        getTitle={getTitle}
        validateForm={validateForm}
      />

      <Modal
        show={showSelectAttributesDialog}
        onHide={() => setShowSelectAttributesDialog(false)}
        scrollable
        size="lg"
        backdrop="static"
        keyboard={false}
        dialogClassName="document-type-select-attributes-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('select_attributes') || 'Odaberi atribute'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">{t('select_attributes_help') || 'Odaberite atribute koje želite da povežete sa tipom dokumenta.'}</p>
          {loadingFields ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm" role="status"></div>
            </div>
          ) : (
            <Table size="sm" hover striped bordered>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>{selectedColumnLabel}</th>
                  <th>{t('name')}</th>
                  <th>{t('type')}</th>
                </tr>
              </thead>
              <tbody>
                {customFields.map((field) => {
                  const selected = sortedConfigs.some((cfg) => cfg.custom_field === field.id)
                  return (
                    <tr key={field.id}>
                      <td>
                        <Form.Check type="checkbox" checked={selected} onChange={(e) => handleFieldToggleInDialog(field.id, e.target.checked)} />
                      </td>
                      <td>{field.name}</td>
                      <td>{field.data_type}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="me-auto text-muted small">
            {t('selected_count') || 'Odabrano'}: {sortedConfigs.length}
          </div>
          <Button variant="outline-secondary" onClick={() => setShowSelectAttributesDialog(false)}>
            {t('close') || 'Zatvori'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
